import {
  signIn as amplifySignIn,
  confirmSignIn as amplifyConfirmSignIn,
  signOut as amplifySignOut,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
} from "aws-amplify/auth";
import type { AuthUser, SignInResult } from "./types";

export const isStub = import.meta.env.VITE_AUTH_STUB === "true";

if (!isStub && (!import.meta.env.VITE_COGNITO_DOCTOR_POOL_ID || import.meta.env.VITE_COGNITO_DOCTOR_POOL_ID === "placeholder")) {
  throw new Error(
    "VITE_COGNITO_DOCTOR_POOL_ID is not configured. " +
    "Set VITE_AUTH_STUB=true for local development or provide a valid pool ID.",
  );
}

function mapNextStep(signInStep: string, missingAttributes?: string[]): SignInResult {
  switch (signInStep) {
    case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
    case "CONFIRM_SIGN_IN_WITH_SMS_MFA_CODE":
      return { nextStep: "CONFIRM_SIGN_IN_WITH_EMAIL_CODE", isSignedIn: false };
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
      return { nextStep: "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD", isSignedIn: false, missingAttributes };
    case "DONE":
      return { nextStep: "DONE", isSignedIn: true };
    default:
      throw new Error(`Unsupported authentication step: ${signInStep}. Please try again or contact support.`);
  }
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  if (isStub) {
    if (email === "locked@test.com") {
      throw new Error("User is disabled.");
    }
    if (email === "newuser@test.com") {
      return { nextStep: "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD", isSignedIn: false };
    }
    return { nextStep: "CONFIRM_SIGN_IN_WITH_EMAIL_CODE", isSignedIn: false };
  }

  try {
    const { nextStep } = await amplifySignIn({ username: email, password });
    const missing = "missingAttributes" in nextStep
      ? (nextStep.missingAttributes as string[])
      : undefined;
    return mapNextStep(nextStep.signInStep, missing);
  } catch (err) {
    const name = err instanceof Error ? err.name : "";
    if (name === "UserAlreadyAuthenticatedException") {
      try {
        await amplifySignOut();
      } catch {
        // sign-out failure is non-fatal; proceed with retry
      }
      const { nextStep } = await amplifySignIn({ username: email, password });
      const missing = "missingAttributes" in nextStep
        ? (nextStep.missingAttributes as string[])
        : undefined;
      return mapNextStep(nextStep.signInStep, missing);
    }
    throw err;
  }
}

export async function confirmSignInWithCode(code: string, email?: string): Promise<SignInResult> {
  if (isStub) {
    if (code === "000000") {
      throw new Error("CodeMismatchException");
    }
    if (email) stubSetSignedIn(email);
    return { nextStep: "DONE", isSignedIn: true };
  }

  const { nextStep } = await amplifyConfirmSignIn({ challengeResponse: code });
  return mapNextStep(nextStep.signInStep);
}

export async function confirmSignInWithNewPassword(
  password: string,
  userAttributes?: Record<string, string>,
): Promise<SignInResult> {
  if (isStub) {
    return { nextStep: "CONFIRM_SIGN_IN_WITH_EMAIL_CODE", isSignedIn: false };
  }

  const result = await amplifyConfirmSignIn({
    challengeResponse: password,
    options: userAttributes ? { userAttributes } : undefined,
  });
  return mapNextStep(result.nextStep.signInStep);
}

export async function forgotPassword(email: string): Promise<void> {
  if (isStub) {
    return;
  }

  await amplifyResetPassword({ username: email });
}

export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  if (isStub) {
    if (code === "000000") {
      throw new Error("CodeMismatchException");
    }
    return;
  }

  await amplifyConfirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}

export async function signOut(): Promise<void> {
  if (isStub) {
    sessionStorage.removeItem("stub-auth-user");
    return;
  }
  await amplifySignOut();
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  if (isStub) {
    const stored = sessionStorage.getItem("stub-auth-user");
    if (stored) return JSON.parse(stored) as AuthUser;
    return null;
  }

  try {
    const user = await amplifyGetCurrentUser();
    let displayName = "";
    try {
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      if (payload) {
        const given = (payload.given_name as string) ?? "";
        const family = (payload.family_name as string) ?? "";
        if (given || family)
          displayName = `Dr. ${given} ${family}`.trim();
      }
    } catch {
      /* name extraction is best-effort */
    }
    return {
      email: user.signInDetails?.loginId ?? user.username,
      userId: user.userId,
      displayName: displayName || undefined,
    };
  } catch {
    return null;
  }
}

function stubSetSignedIn(email: string): void {
  if (isStub) {
    sessionStorage.setItem(
      "stub-auth-user",
      JSON.stringify({ email, userId: "stub-user-id" }),
    );
  }
}
